import { API_URL } from '../config/api';

class ApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_URL;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const sessionID = this.getSessionID();

    const config: RequestInit = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        // Send session_id in header as fallback (cookie is primary, but some setups need header)
        ...(sessionID && { 'X-Session-ID': sessionID }),
        ...options.headers,
      },
      credentials: 'include', // Important: include cookies (session_id cookie)
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        // Session expired or invalid - redirect to login
        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('session_id');
          window.location.href = '/login';
          return new Promise(() => {}) as any; // Prevent further execution during redirect
        }

        const error = await response.json().catch(() => ({ message: 'Request failed' }));
        const errorMessage = error.message || `HTTP error! status: ${response.status}`;
        const httpError = new Error(errorMessage);
        (httpError as any).status = response.status;
        throw httpError;
      }
      return response.json();
    } catch (error: any) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error(`Network error: Unable to reach API at ${url}`);
      }
      throw error;
    }
  }

  private getSessionID(): string | null {
    // Get session ID from localStorage (cookie is the source of truth, but we store it for reference)
    // The session_id cookie is set by backend and sent automatically with credentials: 'include'
    return localStorage.getItem('session_id');
  }

  // Tenant APIs
  async listTenants() {
    return this.get<{ message: string; status: string; data: any[] }>('/tenants');
  }

  async createTenant(name: string) {
    return this.post<{ message: string; status: string; data: any }>('/tenants', { name });
  }

  async getTenant(tenantId: string) {
    return this.get<{ message: string; status: string; data: any }>(`/tenants/${encodeURIComponent(tenantId)}`);
  }

  // Solution Template APIs
  async listSolutionTemplates() {
    return this.get<{ message: string; status: string; data: Array<{ code: string; name: string; createdAt?: string; updatedAt?: string }> }>(
      '/solution-templates'
    );
  }

  async createSolutionTemplate(code: string, name: string) {
    return this.post<{ message: string; status: string; data: any }>(
      '/solution-templates',
      { code, name }
    );
  }

  async updateSolutionTemplate(code: string, name: string) {
    return this.put<{ message: string; status: string; data: any }>(
      `/solution-templates/${encodeURIComponent(code)}`,
      { name }
    );
  }

  async deleteSolutionTemplate(code: string) {
    return this.delete<{ message: string; status: string; data: any }>(
      `/solution-templates/${encodeURIComponent(code)}`
    );
  }

  // Solution Assignment APIs
  async listAvailableSolutions() {
    return this.get<{ message: string; status: string; data: Array<{ code: string; name: string }> }>(
      '/solutions'
    );
  }

  async assignSolution(tenantId: string, solutionCode: string) {
    return this.post<{ message: string; status: string; data: any }>(
      `/tenants/${encodeURIComponent(tenantId)}/solutions/assign`,
      { solutionCode }
    );
  }

  async listSolutions(tenantId: string) {
    return this.get<{ message: string; status: string; data: any[] }>(
      `/tenants/${encodeURIComponent(tenantId)}/solutions`
    );
  }

  // Deprecated - kept for backward compatibility
  async createSolution(tenantId: string, name: string) {
    return this.post<{ message: string; status: string; data: any }>(
      `/tenants/${encodeURIComponent(tenantId)}/solutions`,
      { name }
    );
  }

  // Unit APIs
  async createUnit(tenantId: string, solutionId: string, name: string) {
    return this.post<{ message: string; status: string; data: any }>(
      `/tenants/${encodeURIComponent(tenantId)}/solutions/${encodeURIComponent(solutionId)}/units`,
      { name }
    );
  }

  async listUnits(tenantId: string, solutionId: string) {
    return this.get<{ message: string; status: string; data: any[] }>(
      `/tenants/${encodeURIComponent(tenantId)}/solutions/${encodeURIComponent(solutionId)}/units`
    );
  }

  // User APIs
  async createTenantAdmin(tenantId: string, cognitoUsername: string, email: string, displayName: string) {
    return this.post<{ message: string; status: string; data: any }>(
      `/tenants/${encodeURIComponent(tenantId)}/admin`,
      { cognitoUsername, email, displayName }
    );
  }

  async updateTenantAdmin(tenantId: string, email?: string, displayName?: string) {
    return this.put<{ message: string; status: string; data: any }>(
      `/tenants/${encodeURIComponent(tenantId)}/admin`,
      { email, displayName }
    );
  }

  async listUsers(tenantId: string) {
    return this.get<{ message: string; status: string; data: any[] }>(
      `/tenants/${encodeURIComponent(tenantId)}/users`
    );
  }

  // Unit User Assignment APIs
  async assignUserToUnit(
    tenantId: string,
    solutionId: string,
    unitId: string,
    cognitoUsername: string,
    email: string,
    displayName?: string,
    password?: string
  ) {
    return this.post<{ message: string; status: string; data: any }>(
      `/tenants/${encodeURIComponent(tenantId)}/solutions/${encodeURIComponent(solutionId)}/units/${encodeURIComponent(unitId)}/assign`,
      { cognitoUsername, email, displayName, password }
    );
  }

  async getUnitUser(tenantId: string, solutionId: string, unitId: string) {
    return this.get<{ message: string; status: string; data: any }>(
      `/tenants/${encodeURIComponent(tenantId)}/solutions/${encodeURIComponent(solutionId)}/units/${encodeURIComponent(unitId)}/user`
    );
  }

  async unassignUserFromUnit(tenantId: string, solutionId: string, unitId: string) {
    return this.delete<{ message: string; status: string; data: any }>(
      `/tenants/${encodeURIComponent(tenantId)}/solutions/${encodeURIComponent(solutionId)}/units/${encodeURIComponent(unitId)}/unassign`
    );
  }

  async getUserUnits(cognitoUsername: string) {
    return this.get<{ message: string; status: string; data: any[] }>(
      `/users/${cognitoUsername}/units`
    );
  }

  // Unit Status APIs
  async updateUnitStatus(tenantId: string, solutionId: string, unitId: string, disabled: boolean) {
    return this.put<{ message: string; status: string; data: any }>(
      `/tenants/${encodeURIComponent(tenantId)}/solutions/${encodeURIComponent(solutionId)}/units/${encodeURIComponent(unitId)}/status`,
      { disabled }
    );
  }

  // Unit DB Config APIs
  async updateUnitDbConfig(tenantId: string, solutionId: string, unitId: string, config: any) {
    return this.put<{ message: string; status: string; data: any }>(
      `/tenants/${encodeURIComponent(tenantId)}/solutions/${encodeURIComponent(solutionId)}/units/${encodeURIComponent(unitId)}/db-config`,
      config
    );
  }

  async getUnitDbConfig(tenantId: string, solutionId: string, unitId: string) {
    return this.get<{ message: string; status: string; data: any }>(
      `/tenants/${encodeURIComponent(tenantId)}/solutions/${encodeURIComponent(solutionId)}/units/${encodeURIComponent(unitId)}/db-config`
    );
  }

  // Unit Search APIs
  async searchUnitsByCognitoUser(cognitoUsername: string) {
    return this.get<{ message: string; status: string; data: any[] }>(
      `/units/search?cognitoUsername=${encodeURIComponent(cognitoUsername)}`
    );
  }

  async searchUnitsByHierarchy(hierarchyPattern: string) {
    return this.get<{ message: string; status: string; data: any[] }>(
      `/units/search?hierarchyPattern=${encodeURIComponent(hierarchyPattern)}`
    );
  }

  // SQS Queue APIs
  async listSqsQueues() {
    return this.get<{ message: string; status: string; data: { environment: string; queues: any[] } }>('/sqs');
  }

  async toggleSqsQueue(payload: { queueName: string; enabled: boolean; uuid?: string }) {
    return this.post<{ message: string; status: string; data: any }>('/sqs/toggle', payload);
  }

  private async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  private async post<T>(endpoint: string, data: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  private async put<T>(endpoint: string, data: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  private async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

export const apiService = new ApiService();

